import { nowEpochMs } from "./datetime";
import { toEpochMs } from "./utcOnly";
function toEpochMsSafe(value) {
    if (value == null)
        return undefined;
    if (typeof value === "number") {
        return value;
    }
    try {
        if (value instanceof Date || typeof value === "string") {
            return toEpochMs(value);
        }
        // Unknown object shape; give Date.parse a chance below.
        const maybeObject = value;
        if (typeof maybeObject === "object" &&
            maybeObject !== null &&
            "toString" in maybeObject) {
            const stringValue = maybeObject.toString();
            return toEpochMs(stringValue);
        }
    }
    catch {
        const parsed = typeof value === "string" ? Date.parse(value) : NaN;
        return Number.isFinite(parsed) ? parsed : undefined;
    }
}
/**
 * Compute the canonical campaign status based on schedule.
 * Suspended is always sticky. Throws when required schedule fields are absent.
 *
 * Rules:
 * - startDate and endDate are required for scheduled/live/ended status.
 * - Campaigns without dates remain "draft".
 * - before start -> "scheduled"
 * - between start/end -> "live"
 * - after end OR endedAt in the past -> "ended"
 * - NFT campaigns (rewardMethod === "completion-nft") require nftContractDeployedAt to leave "draft"
 */
export function computeCampaignStatus(campaign, now = nowEpochMs()) {
    const status = (campaign.status ?? "draft");
    if (["suspended", "ended"].includes(status))
        return status;
    const startMs = toEpochMsSafe(campaign.startDate);
    const endMs = toEpochMsSafe(campaign.endDate);
    // Campaigns without dates remain in draft
    if (!startMs || !endMs) {
        return "draft";
    }
    // NFT campaigns require contract deployment to go live
    const rewardConfig = campaign.metadata?.rewardConfig;
    const isNftCampaign = rewardConfig?.rewardMethod === "completion-nft";
    const nftDeployed = Boolean(toEpochMsSafe(rewardConfig?.nftContractDeployedAt));
    if (isNftCampaign && !nftDeployed)
        return "draft";
    const nowMs = toEpochMsSafe(now) ?? nowEpochMs();
    const endedAtMs = toEpochMsSafe(campaign.endedAt);
    const isEnded = (endedAtMs ? nowMs >= endedAtMs : false) || nowMs >= endMs;
    if (isEnded)
        return "ended";
    return nowMs < startMs ? "scheduled" : "live";
}
/**
 * Determine if the persisted status should change and what the next status is.
 * Returns the computed canonical status and a flag indicating if it differs
 * from the stored status.
 */
export function getCampaignStatusTransition(campaign, now = nowEpochMs()) {
    const nextStatus = computeCampaignStatus(campaign, now);
    const currentStatus = (campaign.status ??
        "draft");
    return {
        nextStatus,
        shouldUpdate: nextStatus !== currentStatus,
    };
}
/**
 * Defense-in-depth helper to surface the latest correct status to any caller.
 * This uses the canonical computation (ignores stale persisted values).
 */
export function getLatestCampaignStatus(campaign, now = nowEpochMs()) {
    return computeCampaignStatus(campaign, now);
}
/**
 * Check if the reward configuration is ready for a campaign to go live / be published.
 * Token campaigns no longer require deposits. NFT campaigns require contract deployment.
 */
export function isRewardPublishReady(rewardConfig) {
    if (!rewardConfig)
        return false;
    if (rewardConfig.rewardMethod === "completion-nft") {
        return Boolean(rewardConfig.nftContractAddress &&
            toEpochMsSafe(rewardConfig.nftContractDeployedAt));
    }
    return true;
}
